import { io, Socket } from 'socket.io-client';

// Socket.IO needs base URL without /api/v1 path (that's interpreted as a namespace)
const getSocketUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'https://api.trademagic.gg';
  // Remove /api/v1 suffix if present
  return apiUrl.replace(/\/api\/v1\/?$/, '');
};

const SOCKET_URL = getSocketUrl();

/**
 * Singleton socket manager for shared Socket.IO connection.
 * Used by both activity feed and messaging features.
 *
 * Auth: Uses auth-token cookie automatically (withCredentials: true)
 * Rooms: Backend joins authenticated users to user-${userId} room
 */
class SocketManager {
  private socket: Socket | null = null;
  private refCount = 0;
  private connectionListeners: Set<(connected: boolean) => void> = new Set();
  private disconnectTimeout: NodeJS.Timeout | null = null;

  /**
   * Get the socket instance, creating if needed.
   * Does NOT increment ref count - use acquireSocket() for that.
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Acquire a reference to the socket connection.
   * Creates the socket if this is the first consumer.
   * Returns the socket instance.
   */
  acquireSocket(): Socket {
    // Cancel pending disconnect if reacquiring (handles React StrictMode double-render)
    if (this.disconnectTimeout) {
      clearTimeout(this.disconnectTimeout);
      this.disconnectTimeout = null;
    }

    this.refCount++;

    if (!this.socket) {
      this.socket = io(SOCKET_URL, {
        path: '/socket.io/',
        transports: ['websocket', 'polling'],
        withCredentials: true, // Send auth-token cookie
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 8000,
        timeout: 20000,
      });

      // Log connection events for debugging
      this.socket.on('connect', () => {
        console.log('[SocketManager] Connected, socket id:', this.socket?.id);
        this.notifyConnectionListeners(true);
      });

      this.socket.on('disconnect', (reason) => {
        console.log('[SocketManager] Disconnected:', reason);
        this.notifyConnectionListeners(false);
      });

      this.socket.on('connect_error', (error) => {
        console.error('[SocketManager] Connection error:', error.message);
      });

      this.socket.on('reconnect_attempt', (attemptNumber) => {
        console.log('[SocketManager] Reconnect attempt:', attemptNumber);
      });

      this.socket.on('reconnect_failed', () => {
        console.error('[SocketManager] Reconnection failed after max attempts');
      });
    }

    return this.socket;
  }

  /**
   * Release a reference to the socket connection.
   * Disconnects the socket when the last consumer releases.
   * Uses delayed disconnect to handle React StrictMode double-render.
   */
  releaseSocket(): void {
    this.refCount = Math.max(0, this.refCount - 1);

    if (this.refCount === 0 && this.socket) {
      // Delay disconnect to handle StrictMode double-render
      // If component remounts within 100ms, acquireSocket() cancels this timeout
      this.disconnectTimeout = setTimeout(() => {
        if (this.refCount === 0 && this.socket) {
          console.log('[SocketManager] Last consumer released, disconnecting');
          this.socket.removeAllListeners();
          this.socket.disconnect();
          this.socket = null;
        }
      }, 100);
    }
  }

  /**
   * Check if socket is currently connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Add a listener for connection state changes
   */
  addConnectionListener(listener: (connected: boolean) => void): void {
    this.connectionListeners.add(listener);
  }

  /**
   * Remove a connection state listener
   */
  removeConnectionListener(listener: (connected: boolean) => void): void {
    this.connectionListeners.delete(listener);
  }

  private notifyConnectionListeners(connected: boolean): void {
    this.connectionListeners.forEach((listener) => listener(connected));
  }

  /**
   * Get current reference count (for debugging)
   */
  getRefCount(): number {
    return this.refCount;
  }
}

// Export singleton instance
export const socketManager = new SocketManager();
